import { useState, useEffect } from 'react'
import {
  Table,
  Button,
  Space,
  Modal,
  message,
  Popconfirm,
  Tag,
  Tooltip,
  Dropdown,
  MenuProps
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ImportOutlined,
  ExportOutlined,
  DownOutlined,
  ReloadOutlined
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { PageHeader } from '../../components/common'
import { SKUForm, SKUFilter, SKUImportModal } from '../../components/SKU'
import { SKU, CreateSKUForm, SKUFilter as SKUFilterType, PaginationParams } from '../../types'
import { formatDateTime } from '../../utils'
import skuService from '../../services/sku'

const SKUManagement = () => {
  const [skus, setSKUs] = useState<SKU[]>([])
  const [loading, setLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    pageSize: 20
  })
  const [filter, setFilter] = useState<SKUFilterType>({})
  
  // 模态框状态
  const [createModalVisible, setCreateModalVisible] = useState(false)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [importModalVisible, setImportModalVisible] = useState(false)
  const [editingSKU, setEditingSKU] = useState<SKU | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    loadSKUs()
  }, [pagination, filter])

  const loadSKUs = async () => {
    setLoading(true)
    try {
      const result = await skuService.getSKUList(filter, pagination)
      setSKUs(result.data)
      setTotal(result.total)
    } catch (error) {
      // 错误处理已在service层处理
    } finally {
      setLoading(false)
    }
  }

  const handleTableChange = (page: number, pageSize: number) => {
    setPagination({ page, pageSize })
  }

  const handleFilter = (newFilter: SKUFilterType) => {
    setFilter(newFilter)
    setPagination({ ...pagination, page: 1 })
  }

  const handleCreate = async (values: CreateSKUForm) => {
    setSubmitting(true)
    try {
      await skuService.createSKU(values)
      message.success('SKU创建成功')
      setCreateModalVisible(false)
      loadSKUs()
    } catch (error) {
      // 错误处理已在service层处理
    } finally {
      setSubmitting(false)
    }
  }

  const handleEdit = async (values: CreateSKUForm) => {
    if (!editingSKU) return
    
    setSubmitting(true)
    try {
      await skuService.updateSKU(editingSKU.id, values)
      message.success('SKU更新成功')
      setEditModalVisible(false)
      setEditingSKU(null)
      loadSKUs()
    } catch (error) {
      // 错误处理已在service层处理
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await skuService.deleteSKU(id)
      message.success('SKU删除成功')
      loadSKUs()
    } catch (error) {
      // 错误处理已在service层处理
    }
  }

  const handleExport = async (exportType: 'filtered' | 'all' | 'template') => {
    try {
      if (exportType === 'template') {
        await skuService.exportTemplate()
      } else {
        await skuService.exportSKUs({
          ...filter,
          exportType,
          ...(exportType === 'filtered' ? pagination : {})
        })
      }
      message.success('导出成功')
    } catch (error) {
      // 错误处理已在service层处理
    }
  }

  const exportMenuItems: MenuProps['items'] = [
    {
      key: 'filtered',
      label: '导出当前筛选结果',
      onClick: () => handleExport('filtered')
    },
    {
      key: 'all',
      label: '导出全部SKU',
      onClick: () => handleExport('all')
    },
    {
      key: 'template',
      label: '下载导入模板',
      onClick: () => handleExport('template')
    }
  ]

  const columns: ColumnsType<SKU> = [
    {
      title: '条形码',
      dataIndex: 'barcode',
      key: 'barcode',
      width: 120,
      fixed: 'left',
      render: (text) => (
        <Tooltip title={text}>
          <span style={{ fontFamily: 'monospace' }}>{text}</span>
        </Tooltip>
      )
    },
    {
      title: '款号',
      dataIndex: 'styleNumber',
      key: 'styleNumber',
      width: 120,
    },
    {
      title: '款名',
      dataIndex: 'styleName',
      key: 'styleName',
      width: 200,
      ellipsis: {
        showTitle: false,
      },
      render: (text) => (
        <Tooltip title={text}>
          {text}
        </Tooltip>
      )
    },
    {
      title: '款名简称',
      dataIndex: 'styleShort',
      key: 'styleShort',
      width: 100,
    },
    {
      title: '颜色代码',
      dataIndex: 'colorCode',
      key: 'colorCode',
      width: 100,
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: '颜色描述',
      dataIndex: 'colorDescription',
      key: 'colorDescription',
      width: 120,
    },
    {
      title: '颜色中文',
      dataIndex: 'colorChinese',
      key: 'colorChinese',
      width: 100,
    },
    {
      title: '尺码',
      dataIndex: 'size',
      key: 'size',
      width: 80,
      render: (text) => <Tag color="green">{text}</Tag>
    },
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
      width: 100,
    },
    {
      title: '创建方式',
      dataIndex: 'creationMethod',
      key: 'creationMethod',
      width: 100,
      render: (text) => {
        const color = text === 'manual' ? 'orange' : 'purple'
        const label = text === 'manual' ? '手动' : '导入'
        return <Tag color={color}>{label}</Tag>
      }
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (text) => formatDateTime(text)
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingSKU(record)
              setEditModalVisible(true)
            }}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个SKU吗？"
            description="删除后无法恢复，请谨慎操作。"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div>
      <PageHeader
        title="SKU管理"
        extra={[
          <Button
            key="refresh"
            icon={<ReloadOutlined />}
            onClick={loadSKUs}
            loading={loading}
          >
            刷新
          </Button>,
          <Button
            key="import"
            icon={<ImportOutlined />}
            onClick={() => setImportModalVisible(true)}
          >
            导入
          </Button>,
          <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
            <Button icon={<ExportOutlined />}>
              导出 <DownOutlined />
            </Button>
          </Dropdown>,
          <Button
            key="create"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalVisible(true)}
          >
            新建SKU
          </Button>
        ]}
      />

      <SKUFilter onFilter={handleFilter} loading={loading} />

      <Table
        columns={columns}
        dataSource={skus}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1400 }}
        pagination={{
          current: pagination.page,
          pageSize: pagination.pageSize,
          total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          onChange: handleTableChange,
          onShowSizeChange: handleTableChange,
        }}
      />

      {/* 创建SKU模态框 */}
      <Modal
        title="新建SKU"
        open={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        footer={null}
        width={800}
      >
        <SKUForm
          onSubmit={handleCreate}
          onCancel={() => setCreateModalVisible(false)}
          loading={submitting}
        />
      </Modal>

      {/* 编辑SKU模态框 */}
      <Modal
        title="编辑SKU"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false)
          setEditingSKU(null)
        }}
        footer={null}
        width={800}
      >
        <SKUForm
          initialValues={editingSKU || undefined}
          onSubmit={handleEdit}
          onCancel={() => {
            setEditModalVisible(false)
            setEditingSKU(null)
          }}
          loading={submitting}
        />
      </Modal>

      {/* 导入SKU模态框 */}
      <SKUImportModal
        visible={importModalVisible}
        onClose={() => setImportModalVisible(false)}
        onSuccess={loadSKUs}
      />
    </div>
  )
}

export default SKUManagement